import { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { nodeWidth } from '../utils/layout';
import { getLineCountStyle } from '../utils/lineCountStyles';
import FileModal from './FileModal';
import featureFlags from '../config/versions.config';
import { API_BASE_URL } from '../config/constants';
import { getFileExtension, splitFileName } from '../utils/nodeUtils';
import { getFileIcon, getExtensionColor } from '../utils/fileIconUtils';

// Truncate name to max length, adding ellipsis if needed
const truncateName = (name, maxLength = 16) => {
  if (!name || name.length <= maxLength) return name;
  return name.substring(0, maxLength - 1) + '…';
};

const UniversalNode = ({ id, data, selected }) => {
  const hasIncoming = data.hasIncoming || false;
  const hasOutgoing = data.hasOutgoing || false;
  const [isHovered, setIsHovered] = useState(false);
  const [lineCount, setLineCount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Check if node has children and what type they are
  const children = data.children || [];
  const hasChildren = Array.isArray(children) ? children.length > 0 : false;
  
  // Check if this node is a class (from originalNode, path, or className)
  const originalNodeType = data.originalNode?.type;
  const isClassFromPath = data.path && data.path.endsWith('.class');
  const isClassFromClassName = id && typeof id === 'string' && id.includes('.class');
  const isClass = originalNodeType === 'class' || isClassFromPath || isClassFromClassName;
  
  // Check if this node is a method (from originalNode, path, or id)
  const isMethodFromPath = data.path && data.path.endsWith('.method');
  const isMethodFromId = id && typeof id === 'string' && id.includes('.method');
  const isMethod = originalNodeType === 'method' || isMethodFromPath || isMethodFromId;
  
  // Check if this node is a function (from originalNode, path, or id)
  const isFunctionFromPath = data.path && data.path.endsWith('.function');
  const isFunctionFromId = id && typeof id === 'string' && id.includes('.function');
  const isFunction = originalNodeType === 'function' || isFunctionFromPath || isFunctionFromId;
  
  // Check for file extension first to detect empty folders (no suffix = folder)
  const rawFileExtension = getFileExtension(data.path, data.label);
  const hasFileExtension = rawFileExtension !== '';
  
  // Determine if children are all functions/methods/classes (then display as file) or files/folders (then display as folder)
  // Children come from the tree structure and have a 'type' property
  const childrenAreFunctionsMethodsOrClasses = hasChildren && children.every(child => {
    // Check if child has type property directly, or check originalNode
    const childType = child.type || (child.originalNode && child.originalNode.type);
    return childType === 'function' || childType === 'method' || childType === 'class';
  });
  
  // Display as folder if:
  // 1. No file extension (no suffix) - this catches empty folders
  // 2. OR has children that are NOT all functions/methods/classes
  // AND NOT a class
  const isFolder = (!hasFileExtension || (hasChildren && !childrenAreFunctionsMethodsOrClasses)) && !isClass;
  
  const isCollapsed = data.isCollapsed !== undefined ? data.isCollapsed : true; // Default to collapsed
  
  // Get file extension and icon (only used if not displaying as folder, class, method, or function)
  // Classes, methods, and functions don't show file extensions/icons
  const fileExtension = (isFolder || isClass || isMethod || isFunction) ? null : rawFileExtension;
  const fileIcon = (isFolder || isClass || isMethod || isFunction) ? null : getFileIcon(fileExtension);
  
  // Split label into base name and extension for colored suffix
  // For classes and methods, remove the suffix (.class or .method) from the label for display
  let displayLabel = data.label;
  let { baseName, extension } = splitFileName(data.label);
  
  if (isClass && data.label.endsWith('.class')) {
    // Remove .class suffix for display
    displayLabel = data.label.substring(0, data.label.length - 6); // Remove '.class'
    baseName = displayLabel;
  } else if (isMethod && data.label.endsWith('.method')) {
    // For methods, remove .method suffix and also remove class prefix (e.g., "Real.__init__.method" -> "__init__")
    const withoutMethodSuffix = data.label.substring(0, data.label.length - 7); // Remove '.method'
    // Remove class prefix (everything before the last dot before the method name)
    // e.g., "Real.__init__" -> "__init__"
    const lastDotIndex = withoutMethodSuffix.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      displayLabel = withoutMethodSuffix.substring(lastDotIndex + 1);
    } else {
      displayLabel = withoutMethodSuffix;
    }
    baseName = displayLabel;
  }
  
  const extensionColor = (isFolder || isClass || isMethod) ? null : (extension ? getExtensionColor(fileExtension) : null);

  // Handle toggle/expand for nodes with children
  const handleClick = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      // Both folders and files with function children toggle
      if (data.onToggle) {
        data.onToggle(id);
      } else if (data.onExpand) {
        data.onExpand(id);
      }
    }
  };

  // Handle opening file in Cursor or code view
  const handleOpenInCursor = async (e) => {
    e.stopPropagation();
    if (!data.path) return;

    // Check feature flag - if false, open in code view instead of Cursor
    if (!featureFlags.openInCursor) {
      // Use onOpenCodeView callback if available, otherwise fall back to modal
      if (data.onOpenCodeView) {
        data.onOpenCodeView(data.path);
      } else {
        setIsModalOpen(true);
      }
      return;
    }

    try {
      const response = await fetch('${API_BASE_URL}/open-cursor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: data.path, line: 1 })
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to open Cursor');
      }
    } catch (error) {
      console.error('Failed to open in Cursor:', error);
    }
  };

  // Handle deleting node
  const handleDelete = async (e) => {
    e.stopPropagation();
    
    if (!data.path || !data.onDelete) {
      console.warn('No path or delete handler available');
      return;
    }

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${data.label}"?`)) {
      return;
    }

    await data.onDelete(data.path);
  };

  // Handle renaming node
  const handleRename = async (e) => {
    e.stopPropagation();
    
    if (!data.path || !data.onRename) {
      console.warn('No path or rename handler available');
      return;
    }

    // Get current filename
    const currentFileName = data.label;
    const { baseName: currentBaseName, extension: currentExtension } = splitFileName(currentFileName);
    
    // Prompt for new filename
    const newFileName = window.prompt(`Rename "${currentFileName}" to:`, currentFileName);
    
    if (!newFileName || newFileName === currentFileName) {
      return; // User cancelled or didn't change name
    }

    // Validate filename
    if (newFileName.trim() === '') {
      alert('Filename cannot be empty');
      return;
    }

    // Preserve extension if user didn't include it (for files)
    let finalFileName = newFileName;
    if (!isFolder && currentExtension && !newFileName.includes('.')) {
      finalFileName = `${newFileName}${currentExtension}`;
    }

    await data.onRename(data.path, finalFileName);
  };

  // Fetch line count for files (not folders, classes, methods, or functions)
  useEffect(() => {
    if (isFolder || isClass || isMethod || isFunction || !data.path) {
      setLineCount(null);
      return;
    }

    const fetchLineCount = async () => {
      try {
        // Remove root folder from path - server expects relative path without root
        const pathParts = data.path.split('/');
        const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : data.path;
        
        const encodedPath = encodeURIComponent(relativePath);
        const response = await fetch(`${API_BASE_URL}/read?path=${encodedPath}`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          return;
        }

        // Try to parse as JSON first, fall back to plain text
        const contentType = response.headers.get('content-type');
        let fileContent = '';
        
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          fileContent = data.content || data || '';
        } else {
          const text = await response.text();
          try {
            const data = JSON.parse(text);
            fileContent = data.content || data || '';
          } catch (e) {
            fileContent = text;
          }
        }
        
        // Calculate line count from the actual content
        const count = fileContent.length === 0 ? 0 : fileContent.split('\n').length;
        setLineCount(count);
      } catch (err) {
        // Silently fail - line count is optional
        console.error('Error fetching line count:', err);
      }
    };

    fetchLineCount();
  }, [isFolder, isClass, isMethod, isFunction, data.path]);

  // Determine styling based on whether it's a folder or file
  const cursor = hasChildren ? 'pointer' : 'default'; // Make clickable if it has children

  // For folders, use the same structure as FolderNode
  if (isFolder) {
    const fileCount = data.fileCount || 0;
    return (
      <div
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '8px',
          minWidth: `${nodeWidth}px`,
          width: '100%',
          height: '100%',
          userSelect: 'none',
          backgroundColor: '#e6f3ff',
          borderRadius: '8px',
          padding: '8px 12px',
          position: 'relative',
          boxSizing: 'border-box',
          transition: 'opacity 0.2s ease, filter 0.2s ease',
        }}
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ 
          fontSize: '16px', 
          userSelect: 'none', 
          flexShrink: 0,
        }}>
          {isCollapsed ? '📁' : '📂'}
        </span>
        {hasIncoming && (
          <Handle 
            type="target" 
            position={Position.Left}
          />
        )}
        <span
          style={{
            fontSize: '16px',
            userSelect: 'none',
            flex: 1,
            fontWeight: isCollapsed ? 'bold' : 'normal',
          }}
          title={data.label}
        >
          {truncateName(data.label)}
        </span>
        {fileCount > 0 && (
          <span style={{ 
            fontSize: '14px', 
            userSelect: 'none', 
            color: '#666',
            flexShrink: 0,
            marginRight: '4px'
          }}>
            ({fileCount})
          </span>
        )}
        <Handle 
          type="source" 
          position={Position.Right}
        />
      </div>
    );
  }

  // For files (with or without children)
  return (
    <div
      onClick={hasChildren ? handleClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        cursor: cursor,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
        position: 'relative',
        transition: 'opacity 0.2s ease, filter 0.2s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          minWidth: `${nodeWidth}px`,
          cursor: cursor,
          borderRadius: '8px',
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: 'transparent',
          padding: '0',
          transition: 'opacity 0.2s ease, filter 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {hasIncoming && (
            <Handle 
              type="target" 
              position={Position.Left}
            />
          )}
          {extension && (
            <span style={{ color: extensionColor, fontWeight: '600' }}>
              {extension}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          {fileExtension === 'json' && (
            <span style={{
              color: '#22c55e',
              fontWeight: 'bold',
              fontSize: '16px',
              flexShrink: 0,
            }}>
              {'{}'}
            </span>
          )}
          {fileExtension === 'csv' && (
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth="1.5" 
              stroke="currentColor" 
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
                color: '#22c55e'
              }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
            </svg>
          )}
          {fileIcon && (
            <img 
              src={fileIcon} 
              alt={`${fileExtension} icon`}
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              fontSize: '16px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: (hasChildren && isCollapsed) || (isClass && isCollapsed) || (isMethod && isCollapsed) ? 'bold' : 'normal',
              userSelect: 'none',
              flex: 1,
            }}
            title={(isClass || isMethod) ? displayLabel : baseName}
          >
            {truncateName((isClass || isMethod) ? displayLabel : baseName)}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {lineCount !== null && !isFunction && (() => {
            const style = getLineCountStyle(lineCount);
            return (
              <span style={style}>
                {style.icon && <span style={{ marginRight: '2px' }}>{style.icon}</span>}
              </span>
            );
          })()}
          {isHovered && data.path && !isClass && !isMethod && !isFunction && (
            <button
              onClick={handleOpenInCursor}
              style={{
                padding: '2px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={featureFlags.openInCursor ? "Open in Cursor" : "View File"}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth="1.5" 
                stroke="currentColor" 
                style={{ width: '16px', height: '16px' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
          )}
        </div>
        <Handle 
          type="source" 
          position={Position.Right}
        />
      </div>
      {data.path && (
        <FileModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          filePath={data.path}
        />
      )}
    </div>
  );
};

export default UniversalNode;

